/* eslint-disable max-lines-per-function */
import React, { useState, createContext, useMemo, useReducer } from "react";
import { scaleTime, ScaleTime, scaleBand, ScaleBand, scaleLinear, ScaleLinear } from "d3-scale";
import { Duration } from "date-fns";
import { getTickCount } from "./uitls";
import { layerReducer, initialLayerState, initialRangeState, emptyDuration, LayerAction, Layer } from "./reducers";
import useLocalizationD3 from "./hooks/useLocalizationD3";
import useProjectInitialState from "./hooks/useProjectInitialState";
import { useConfigPages } from "../Dashboard/hooks/useConfigPages";
import { WidgetType } from "../FeatureCard/types";

type AxisTimeScale = ScaleTime<number, number>;
export type AxisBandScale = ScaleBand<Date>;

type DateFilterState = {
  saveOnClose: boolean;
  changeSaveOnClose: React.Dispatch<React.SetStateAction<boolean>>;
  isVisibleSettings: boolean;
  setSettingVisibility: React.Dispatch<React.SetStateAction<boolean>>;
  layers: Layer[];
  layerDispatcher: React.Dispatch<LayerAction>;
  formLayers: Layer[];
  formLayerDispatcher: React.Dispatch<LayerAction>;
  rangeDate: [Date, Date];
  setRangeDate: React.Dispatch<React.SetStateAction<Date | [Date, Date]>>;
  formRangeDate: [Date, Date];
  setFormRangeDate: React.Dispatch<React.SetStateAction<Date | [Date, Date]>>;
  axisSize: Size;
  setAxisSize: React.Dispatch<React.SetStateAction<Size>>;
  commonScale: AxisTimeScale;
  bandScale: AxisBandScale;
  tickCount: number;
  durationInterval: Duration;
  setDurationInterval: React.Dispatch<React.SetStateAction<Duration>>;
  formDurationInterval: Duration;
  setFormDurationInterval: React.Dispatch<React.SetStateAction<Duration>>;
  scrollLeft: number;
  setScrollLeft: React.Dispatch<React.SetStateAction<number>>;
  selectedTick: Date;
  setSelectedTick: React.Dispatch<React.SetStateAction<Date>>;
  commonTickScale: ScaleLinear<number, number>;
  tickWidth: number;
};

const DEFAULT_TICK_SIZE_IN_PIXEL = 7;
const axisCanvas = { left: 7, right: 7 };

type Size = { width: number; height: number };
export type Tick = { x: number; date: Date; label: string };

export const DateFilterContext = createContext<DateFilterState>({} as DateFilterState);

DateFilterContext.displayName = "DateFilterContext";

export const DateFilterContextProvider = ({ children }) => {
  const [saveOnClose, changeSaveOnClose] = useState(true);
  const [isVisibleSettings, setSettingVisibility] = useState(false);
  const [dateFilterLayers, layerDispatcher] = useReducer(layerReducer, initialLayerState);
  const [formLayers, formLayerDispatcher] = useReducer(layerReducer, initialLayerState);
  const [rangeDate, setRangeDate] = useState<[Date, Date]>(initialRangeState);
  const [formRangeDate, setFormRangeDate] = useState<[Date, Date]>(initialRangeState);
  const [axisSize, setAxisSize] = useState<Size>({ width: 0, height: 0 });
  const [formDurationInterval, setFormDurationInterval] = useState<Duration>(emptyDuration);
  const [durationInterval, setDurationInterval] = useState<Duration>(emptyDuration);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [selectedTick, setSelectedTick] = useState<Date>(null);
  const { currentPage } = useConfigPages(WidgetType.Dashboard);
  const layers = currentPage?.dateFilter?.layers || dateFilterLayers;

  useProjectInitialState({
    layerDispatcher,
    formLayerDispatcher,
    setRangeDate,
    setFormRangeDate,
    setDurationInterval,
    setFormDurationInterval,
  });

  useLocalizationD3();

  const [commonScale, commonTickScale, tickWidth, tickCount] = useMemo(() => {
    const visiableCanvas = { start: axisCanvas.left, end: axisSize.width - axisCanvas.right };
    const stepCount = getTickCount(rangeDate, durationInterval);

    const maxTickCount = Math.floor((visiableCanvas.end - visiableCanvas.start) / DEFAULT_TICK_SIZE_IN_PIXEL);
    const tickWidthScale = (visiableCanvas.end - visiableCanvas.start) / stepCount;
    // TODO raname canvas range???
    const range =
      stepCount > maxTickCount
        ? [axisCanvas.left, stepCount * DEFAULT_TICK_SIZE_IN_PIXEL + axisCanvas.left]
        : [visiableCanvas.start, visiableCanvas.end - tickWidthScale];

    return [
      scaleTime().domain(rangeDate).range(range).clamp(true),
      scaleLinear()
        .domain(range)
        .range([0, stepCount - 1])
        .clamp(true),
      stepCount > maxTickCount ? DEFAULT_TICK_SIZE_IN_PIXEL : tickWidthScale,
      isFinite(stepCount) ? stepCount : maxTickCount,
    ];
  }, [axisSize.width, durationInterval, rangeDate]);

  const bandScale = useMemo(() => scaleBand<Date>(), []);

  const dateFilterState: DateFilterState = useMemo(
    () => ({
      saveOnClose,
      changeSaveOnClose,
      isVisibleSettings,
      setSettingVisibility,
      layers,
      layerDispatcher,
      rangeDate,
      setRangeDate,
      axisSize,
      setAxisSize,
      tickCount,
      formLayers,
      formLayerDispatcher,
      formRangeDate,
      setFormRangeDate,
      durationInterval,
      setDurationInterval,
      formDurationInterval,
      setFormDurationInterval,
      scrollLeft,
      setScrollLeft,
      commonScale,
      bandScale,
      selectedTick,
      setSelectedTick,
      tickWidth,
      commonTickScale,
    }),
    [
      saveOnClose,
      isVisibleSettings,
      layers,
      rangeDate,
      axisSize,
      tickCount,
      formLayers,
      formRangeDate,
      durationInterval,
      formDurationInterval,
      scrollLeft,
      commonScale,
      bandScale,
      selectedTick,
      tickWidth,
      commonTickScale,
    ],
  );

  return <DateFilterContext.Provider value={dateFilterState}>{children}</DateFilterContext.Provider>;
};
